import { useRef } from "react";
import { savePDF } from "@progress/kendo-react-pdf";

export interface PdfOptions {
  fileName: string;
  paperSize?: string;
  landscape?: boolean;
  margin?: { top: string; left: string; right: string; bottom: string };
  exportWidth?: number;
  scale?: number;
  hideNoPrint?: boolean;
  expandScrollSelector?: string;
}

export function useReportExport() {
  const reportContentRef = useRef<HTMLDivElement>(null);

  const exportToPDF = (options: PdfOptions) => {
    const el = reportContentRef.current;
    if (!el) return;

    const previousWidth = el.style.width;
    const previousMaxWidth = el.style.maxWidth;
    if (options.exportWidth != null) {
      el.style.width = `${options.exportWidth}px`;
      el.style.maxWidth = `${options.exportWidth}px`;
    }

    const hiddenEls = options.hideNoPrint
      ? Array.from(el.querySelectorAll<HTMLElement>(".report-filter-no-print"))
      : [];
    const placeholders = hiddenEls.map((h) => {
      const placeholder = document.createComment("no-print");
      h.parentNode?.replaceChild(placeholder, h);
      return placeholder;
    });

    const scrollEls = options.expandScrollSelector
      ? Array.from(
          el.querySelectorAll<HTMLElement>(options.expandScrollSelector)
        )
      : [];
    const previousScrollStyles = scrollEls.map((s) => ({
      maxHeight: s.style.maxHeight,
      overflow: s.style.overflow,
      overflowX: s.style.overflowX,
      overflowY: s.style.overflowY,
    }));
    scrollEls.forEach((s) => {
      s.style.maxHeight = "none";
      s.style.overflow = "visible";
      s.style.overflowX = "visible";
      s.style.overflowY = "visible";
    });

    const restore = () => {
      el.style.width = previousWidth;
      el.style.maxWidth = previousMaxWidth;
      placeholders.forEach((placeholder, i) => {
        placeholder.parentNode?.replaceChild(hiddenEls[i], placeholder);
      });
      scrollEls.forEach((s, i) => {
        s.style.maxHeight = previousScrollStyles[i].maxHeight;
        s.style.overflow = previousScrollStyles[i].overflow;
        s.style.overflowX = previousScrollStyles[i].overflowX;
        s.style.overflowY = previousScrollStyles[i].overflowY;
      });
    };

    try {
      savePDF(
        el,
        {
          fileName: options.fileName,
          paperSize: options.paperSize ?? "A4",
          landscape: options.landscape ?? true,
          scale: options.scale ?? 1,
          margin: options.margin ?? {
            top: "1.5cm",
            left: "1.5cm",
            right: "1.5cm",
            bottom: "1.5cm",
          },
          keepTogether: ".report-table-row",
        },
        restore
      );
    } catch (err) {
      restore();
      throw err;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return {
    reportContentRef,
    exportToPDF,
    handlePrint,
  };
}
